import { useEffect, useState } from "react";
import { Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { PageHead } from "@/components/desk/DeskLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function TaxonomyPage() {
  const { setCategories } = useSite();
  const [meta, setMeta] = useState({ categories: [], tags: [] });
  const [cat, setCat] = useState({ name_en: "", name_ta: "" });
  const [tag, setTag] = useState("");

  const load = () => api.get("/desk/meta").then((r) => { setMeta(r.data); setCategories(r.data.categories); });
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
      return true;
    } catch (err) {
      toast.error(fmtErr(err));
      return false;
    }
  };

  return (
    <div data-testid="taxonomy-page">
      <PageHead title="Categories & tags" sub="Reporters can submit under any category." />
      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white border border-zinc-200 p-5">
          <h2 className="font-serif-head text-xl font-bold mb-4">Categories</h2>
          <form className="flex flex-wrap gap-2 mb-5" onSubmit={async (e) => {
            e.preventDefault();
            if (await call(() => api.post("/admin/categories", cat), "Category added")) setCat({ name_en: "", name_ta: "" });
          }}>
            <Input data-testid="category-name-en" required placeholder="Name (English)" value={cat.name_en} onChange={(e) => setCat({ ...cat, name_en: e.target.value })} className="flex-1 min-w-[140px] rounded-sm" />
            <Input data-testid="category-name-ta" required placeholder="பெயர் (Tamil)" value={cat.name_ta} onChange={(e) => setCat({ ...cat, name_ta: e.target.value })} className="flex-1 min-w-[140px] rounded-sm lang-ta" />
            <Button data-testid="category-add-btn" className="rounded-sm">Add</Button>
          </form>
          <ul className="divide-y divide-zinc-200">
            {meta.categories.map((c) => (
              <li key={c.id} data-testid={`category-row-${c.slug}`} className="py-2.5 flex items-center justify-between">
                <span><span className="font-semibold">{c.name_en}</span> <span className="lang-ta text-zinc-500 ml-2">{c.name_ta}</span></span>
                <button data-testid={`category-delete-${c.slug}`} className="p-1.5 text-zinc-400 hover:text-red-600" onClick={() => call(() => api.delete(`/admin/categories/${c.id}`), "Category deleted")}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>
        <section className="bg-white border border-zinc-200 p-5">
          <h2 className="font-serif-head text-xl font-bold mb-4">Tags</h2>
          <form className="flex gap-2 mb-5" onSubmit={async (e) => {
            e.preventDefault();
            if (await call(() => api.post("/admin/tags", { name: tag }), "Tag added")) setTag("");
          }}>
            <Input data-testid="tag-name-input" required placeholder="New tag" value={tag} onChange={(e) => setTag(e.target.value)} className="rounded-sm" />
            <Button data-testid="tag-add-btn" className="rounded-sm">Add</Button>
          </form>
          <div className="flex flex-wrap gap-2">
            {meta.tags.map((t) => (
              <span key={t.id} data-testid={`tag-chip-${t.slug}`} className="inline-flex items-center gap-1 border border-zinc-300 px-2 py-1 text-xs font-mono-meta">
                {t.name}
                <button data-testid={`tag-delete-${t.slug}`} onClick={() => call(() => api.delete(`/admin/tags/${t.id}`), "Tag deleted")} className="text-zinc-400 hover:text-red-600"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
